import { Component, inject, Renderer2, type OnInit } from '@angular/core';
import { SimplebarAngularModule } from 'simplebar-angular';
import { LogoBoxComponent } from '@components/logo-box.component';
import { getMenuItems, type MenuItemType } from '@common/menu-meta';
import { basePath } from '@common/constants';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { findAllParent, findMenuItem } from '@core/helper/utils';
import {
  NgbCollapseModule,
  NgbDropdownModule,
  NgbModal,
  type NgbCollapse,
} from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { changesidebarsize } from '@/store/layout/layout-action';
import { getSidebarsize } from '@/store/layout/layout-selector';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthenticationService } from '@core/services/auth.service';
import { EmployeeProfile } from '@core/interface/employee';
import { EmployeeActions } from '@/store/employee/employee.actions';
import { logout } from '@/store/authentication/authentication.actions';
import { ChangePasswordComponent } from '@components/change-password/change-password.component';
import { HealthCheckService } from '@views/health-check/services/health-check.service';
import { environment } from '@environment/environment';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    SimplebarAngularModule,
    NgbDropdownModule,
    LogoBoxComponent,
    CommonModule,
    NgbCollapseModule,
    RouterModule,
  ],
  templateUrl: './sidebar.component.html',
  styles: ``,
})

export class SidebarComponent implements OnInit {
  menuItems: MenuItemType[] = [];
  activeMenuItems: string[] = [];
  showBottomLogo = true;
  loadingMenu: boolean = true;
  showCompanyLogo: boolean = false;
  companyLogoUrl: string | null = null;
  logoReady: boolean = false;
  private modalService = inject(NgbModal);
  private renderer = inject(Renderer2);
  private authService = inject(AuthenticationService);

  router = inject(Router);
  trimmedURL = this.router.url?.replaceAll(
    basePath !== '' ? basePath + '/' : '',
    '/',
  );

  store = inject(Store);
  user: EmployeeProfile | null = null;
  private healthCheckService = inject(HealthCheckService);

  constructor() {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.trimmedURL = this.router.url?.replaceAll(
          basePath !== '' ? basePath + '/' : '',
          '/',
        );
        this._activateMenu();
        setTimeout(() => {
          this.scrollToActive();
        }, 200);
      }
    });
  }

  ngOnInit(): void {
    this.loadingMenu = true;
    this.authService.getUser().subscribe((user) => {
      if (user) {
        this.user = user;
        this.setCompanyLogo(user);
        this.logoReady = true;
      }
      this.initMenu(user || null);
      this.loadHealthCheckFolders();
    });
    // this.initMenu();
  }

  // initMenu(): void {
  //   this.menuItems = MENU_ITEMS;
  // }

  private setCompanyLogo(profile: EmployeeProfile): void {
    const roleName = profile?.file_sharing_role?.name || '';
    const isCustomerRole = roleName.includes('CUSTOMER') && !roleName.includes('ONE');
    const companyImage = (profile as any)?.company?.image;

    if (isCustomerRole && companyImage) {
      const domain = environment.baseURL.replace(/\/api$/, '');
      this.companyLogoUrl = `${domain}/storage/app/public/${companyImage}`;
      this.showCompanyLogo = true;
    } else {
      this.companyLogoUrl = null;
      this.showCompanyLogo = false;
    }
    this.logoReady = true;
  }

  initMenu(profile: EmployeeProfile | null): void {
    this.menuItems = getMenuItems(profile);
    this.loadingMenu = false;

    // Re-activate menu after updating items
    setTimeout(() => {
      this._activateMenu();
      // Scroll to active item after menu is activated
      setTimeout(() => {
        this.scrollToActive();
      }, 100);
    }, 0);
  }

  loadHealthCheckFolders(): void {
    this.healthCheckService.getFolders().subscribe({
      next: (response: any) => {
        const folders = response?.data || [];
        const foldersMenuItem = this.menuItems.find(
          (m) => m.key === 'health-check-folders',
        );
        if (foldersMenuItem) {
          foldersMenuItem.children = this.buildFolderMenuItems(
            folders,
            'health-check-folders',
          );
        }
      },
      error: (err) => {
        console.error('Error loading folders:', err);
      },
    });
  }

  private buildFolderMenuItems(
    folders: any[],
    parentKey: string,
  ): MenuItemType[] {
    return folders.map((folder: any) => {
      const menuItem: MenuItemType = {
        key: `folder-${folder.id}`,
        label: folder.name,
        icon: 'ti ti-folder',
        url: '/health-check',
        queryParams: { folderId: folder.id },
        parentKey: parentKey,
      };

      // Recursively build children if they exist
      const children =
        folder.children ||
        folder.childrenRecursive ||
        folder.children_recursive ||
        [];
      if (children && children.length > 0) {
        menuItem.children = this.buildFolderMenuItems(children, menuItem.key);
        menuItem.collapsed = true;
      }

      return menuItem;
    });
  }

  // Ensure router interprets query params correctly for menu URLs
  getRouterLink(url: string | undefined): string | any[] {
    if (!url) return ['/'];
    const [path] = url.split('?');
    return path || '/';
  }

  getQueryParams(url: string | undefined): Record<string, any> | undefined {
    if (!url) return undefined;
    const idx = url.indexOf('?');
    if (idx === -1) return undefined;
    const search = url.substring(idx + 1);
    const params = new URLSearchParams(search);
    const obj: Record<string, any> = {};
    params.forEach((value, key) => {
      obj[key] = value;
    });
    return Object.keys(obj).length ? obj : undefined;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this._activateMenu();
    });
    setTimeout(() => {
      this.scrollToActive();
    }, 200);
  }

  hasSubmenu(menu: MenuItemType): boolean {
    return menu.children ? true : false;
  }

  scrollToActive(): void {
    // Try multiple selectors to find the active menu item
    const activatedItem = document.querySelector('.side-nav-item a.active, .side-nav-link.active, .nav-link-ref.active');
    if (activatedItem) {
      const simplebarContent = document.querySelector(
        '.sidenav-menu .simplebar-content-wrapper, .sidenav-menu .simplebar-content'
      );
      if (simplebarContent) {
        const activatedItemRect = activatedItem.getBoundingClientRect();
        const simplebarContentRect = simplebarContent.getBoundingClientRect();
        const activatedItemOffsetTop =
          activatedItemRect.top + (simplebarContent as any).scrollTop;
        const centerOffset =
          activatedItemOffsetTop -
          simplebarContentRect.top -
          simplebarContentRect.height / 2 +
          activatedItemRect.height / 2;
        this.scrollTo(simplebarContent, centerOffset, 600);
      }
    }
  }

  easeInOutQuad(t: number, b: number, c: number, d: number): number {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }

  scrollTo(element: Element, to: number, duration: number): void {
    const start = element.scrollTop;
    const change = to - start;
    const increment = 20;
    let currentTime = 0;

    const animateScroll = () => {
      currentTime += increment;
      const val = this.easeInOutQuad(currentTime, start, change, duration);
      element.scrollTop = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };
    animateScroll();
  }

  _activateMenu(): void {
    const div = document.querySelector('.sidenav-menu');

    let matchingMenuItem = null;

    if (div) {
      let items: any = div.getElementsByClassName('nav-link-ref');
      for (let i = 0; i < items.length; ++i) {
        if (
          this.trimmedURL === items[i].pathname ||
          (this.trimmedURL.startsWith('/invoice/') &&
            items[i].pathname === '/invoice/RB6985') ||
          (this.trimmedURL.startsWith('/ecommerce/product/') &&
            items[i].pathname === '/ecommerce/product/1')
        ) {
          matchingMenuItem = items[i];
          break;
        }
      }

      if (matchingMenuItem) {
        const mid = matchingMenuItem.getAttribute('aria-controls');
        const activeMt = findMenuItem(this.menuItems, mid);

        if (activeMt) {
          const matchingObjs = [
            activeMt['key'],
            ...findAllParent(this.menuItems, activeMt),
          ];

          this.activeMenuItems = matchingObjs;
          this.menuItems.forEach((menu: MenuItemType) => {
            menu.collapsed = !matchingObjs.includes(menu.key!);
          });

          // Scroll to active item after menu activation
          setTimeout(() => {
            this.scrollToActive();
          }, 100);
        }
      }
    }
  }

  /**
   * toggles open menu
   * @param menuItem clicked menuitem
   * @param collapse collpase instance
   */
  toggleMenuItem(menuItem: MenuItemType, collapse: NgbCollapse): void {
    collapse.toggle();
    let openMenuItems: string[];
    if (!menuItem.collapsed) {
      openMenuItems = [
        menuItem['key'],
        ...findAllParent(this.menuItems, menuItem),
      ];
      this.menuItems.forEach((menu: MenuItemType) => {
        if (!openMenuItems.includes(menu.key!)) {
          menu.collapsed = true;
        }
      });
    }
  }

  changeSidebarSize() {

    let size = document.documentElement.getAttribute('data-sidenav-size');
    if (size == 'sm-hover') {
      size = 'sm-hover-active';
    } else {
      size = 'sm-hover';
    }
    this.store.dispatch(changesidebarsize({ size }));
    this.store.select(getSidebarsize).subscribe((size) => {
      document.documentElement.setAttribute('data-sidenav-size', size);
    });
  }

  onToggleMobileMenu() {
    this.store.select(getSidebarsize).subscribe((size: any) => {
      document.documentElement.setAttribute('data-sidenav-size', size);
    });

    const size = document.documentElement.getAttribute('data-sidenav-size');

    document.documentElement.classList.toggle('sidebar-enable');
    if (size != 'full') {
      if (document.documentElement.classList.contains('sidebar-enable')) {
        this.store.dispatch(changesidebarsize({ size: 'condensed' }));
      } else {
        this.store.dispatch(changesidebarsize({ size: 'default' }));
      }
    } else {
      this.showBackdrop();
    }
  }

  showBackdrop() {
    const backdrop = this.renderer.createElement('div');
    this.renderer.addClass(backdrop, 'offcanvas-backdrop');
    this.renderer.addClass(backdrop, 'fade');
    this.renderer.addClass(backdrop, 'show');
    this.renderer.appendChild(document.body, backdrop);
    this.renderer.setStyle(document.body, 'overflow', 'hidden');

    if (window.innerWidth > 1040) {
      this.renderer.setStyle(document.body, 'paddingRight', '15px');
    }

    this.renderer.listen(backdrop, 'click', () => {
      document.documentElement.classList.remove('sidebar-enable');
      this.renderer.removeChild(document.body, backdrop);
      this.renderer.setStyle(document.body, 'overflow', null);
      this.renderer.setStyle(document.body, 'paddingRight', null);
    });
  }
  logout() {
    this.store.dispatch(EmployeeActions.employeesLogout());
    this.store.dispatch(logout());
  }

  changePassword() {
    this.modalService.dismissAll();
    this.modalService.open(ChangePasswordComponent, { size: 'lg' });
  }
}
